import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Menu, PawPrint, Phone } from 'lucide-react';
import { BRAND } from '../../data/mock';

export const Header = ({ onContactClick }) => {
  const [isOpen, setIsOpen] = useState(false);

  const navItems = [
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Why Us', href: '#why-different' },
    { label: 'Coverage', href: '#customization' },
    { label: 'States We Serve', href: '#states' },
    { label: 'About', href: '#about' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
              <PawPrint className="w-5 h-5 text-white" />
            </div>
            <div className="hidden sm:block">
              <span className="text-lg font-bold text-slate-800">Local Pet</span>
              <span className="text-lg font-bold text-teal-600"> Advisors</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                className="text-sm font-medium text-slate-600 hover:text-teal-600 transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-teal-500 hover:after:w-full after:transition-all"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href={`tel:${BRAND.phone}`}
              className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-teal-600 transition-colors"
            >
              <Phone className="w-4 h-4" />
              Talk to an Advisor
            </a>
            <Button
              asChild
              className="bg-gradient-to-r from-orange-500 to-coral-500 hover:from-orange-600 hover:to-coral-600 text-white font-semibold px-6 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all"
              style={{ background: 'linear-gradient(135deg, #f97316 0%, #fb7185 100%)' }}
            >
              <a href={BRAND.quoteUrl} target="_blank" rel="noopener noreferrer">
                Get Free Quote
              </a>
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="w-6 h-6 text-slate-700" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <div className="flex flex-col gap-6 mt-8">
                <a href="/" className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                    <PawPrint className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-lg font-bold text-slate-800">Local Pet Advisors</span>
                </a>
                <nav className="flex flex-col gap-4">
                  {navItems.map((item) => (
                    <button
                      key={item.label}
                      onClick={() => scrollToSection(item.href)}
                      className="text-left text-base font-medium text-slate-600 hover:text-teal-600 transition-colors py-2 border-b border-slate-100"
                    >
                      {item.label}
                    </button>
                  ))}
                </nav>
                <div className="flex flex-col gap-3 mt-4">
                  <Button
                    variant="outline"
                    className="w-full justify-center"
                    asChild
                  >
                    <a href={`tel:${BRAND.phone}`}>
                      <Phone className="w-4 h-4 mr-2" />
                      Talk to an Advisor
                    </a>
                  </Button>
                  <Button
                    asChild
                    className="w-full justify-center text-white font-semibold"
                    style={{ background: 'linear-gradient(135deg, #f97316 0%, #fb7185 100%)' }}
                  >
                    <a href={BRAND.quoteUrl} target="_blank" rel="noopener noreferrer">
                      Get Free Quote
                    </a>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
